import React, { useCallback, useState, useMemo } from 'react'
import { Formik, Form } from 'formik'
import { useMutation } from '@apollo/client'
import { useHistory } from 'react-router'

import {
  PLACE_APPLE_PAY_ORDER,
  GET_CART_FOR_CHECKOUT
} from '../../apollo/cart/cart.graphql'
import { dataByTypename } from '../../utils/apollo-helpers'
import { useGetCart } from '../CartQuery/CartQuery'
import { Fieldset } from '@local/do-secundo-fieldset'
import { CartTable } from '../Cart/CartTable/CartTable'
import { CartFooter } from '../Cart/CartFooter/CartFooter'
import { useRestaurant } from '@local/do-secundo-restaurant-provider'
import {
  useApplePay,
  contactInfoSchema,
  useFormikValues
} from './checkout-helpers'
import { makeApplePayPayment } from '../ApplePay/apple-pay-utils'
import { useSavedAddresses } from '../use-saved-addresses/use-saved-addresses'
import { Progress } from '@local/do-secundo-progress'
import * as CheckoutDeliveryInfo from '../CheckoutForm/CheckoutDeliveryInfo/CheckoutDeliveryInfo'
import * as Tip from '../CheckoutForm/Tip/Tip'
import * as CurbsidePickup from '../CheckoutForm/CurbsidePickup/CurbsidePickup'
import * as DeliveryCommunicationConsent from '../CheckoutForm/DeliveryCommunicationConsent/DeliveryCommunicationConsent'

import { ErrorComponent } from '@local/do-secundo-error'
import { useFulfillment } from '../FulfillmentProvider/FulfillmentProvider'
import { useTrackPurchase } from '../../utils/checkout-helpers'
import { useCurbsidePickup } from '../use-curbside-pickup/use-curbside-pickup'
import * as RoundUpForCharity from '../CheckoutForm/RoundUpForCharity/RoundUpForCharity'
import styles from './CheckoutApplePay.module.css'
import { getRawPhoneNumber } from '@local/do-secundo-form-utils'
import { useTfgContext } from '../TfgContextProvider/TfgContextProvider'
import { LegalCopy } from '../LegalCopy/LegalCopy'
import { SmsCommunicationConsent } from '../CheckoutForm/SmsCommunicationConsent/SmsCommunicationConsent'

const formModules = [
  CheckoutDeliveryInfo,
  CurbsidePickup,
  DeliveryCommunicationConsent,
  Tip,
  RoundUpForCharity
]
export const CheckoutApplePay = () => {
  const featureFlags = {}
  const { curbsidePickupConfig, ...curbside } = useCurbsidePickup()
  const trackPurchase = useTrackPurchase()
  const [errorMessage, setErrorMessage] = useState()
  const fulfillmentContext = useFulfillment()
  const { savedAddresses } = useSavedAddresses()
  const { loading, error, getConfig, getMerchantSession } = useApplePay()
  const history = useHistory()
  const { getRestaurantPath } = useRestaurant()
  const { setTfgConfig } = useTfgContext()
  const [checkout] = useMutation(PLACE_APPLE_PAY_ORDER)
  const {
    cart,
    deleteCartGuid,
    loading: cartLoading,
    error: cartError
  } = useGetCart(GET_CART_FOR_CHECKOUT)
  const context = useMemo(
    () => ({
      cart,
      featureFlags,
      fulfillmentContext,
      savedAddresses,
      curbsidePickupConfig
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cart, fulfillmentContext, savedAddresses, curbsidePickupConfig]
  )

  const { initialValues, validationSchema } = useFormikValues({
    context,
    formModules
  })

  const getPerformTransaction = useCallback(
    (values, { setSubmitting }) =>
      async (payment) => {
        const { shippingContact } = payment
        shippingContact.phoneNumber = getRawPhoneNumber(
          shippingContact.phoneNumber
        )

        try {
          await contactInfoSchema.validate(shippingContact)

          const initialContactInfo = {
            cartGuid: cart.guid,
            pkPaymentToken: JSON.stringify(payment),
            customer: {
              email: shippingContact.emailAddress,
              firstName: shippingContact.givenName,
              lastName: shippingContact.familyName,
              phone: shippingContact.phoneNumber
            }
          }
          const response = await checkout({
            variables: {
              input: formModules.reduce((acc, formModule) => {
                return {
                  ...acc,
                  ...formModule.getArgsForSubmit({ ...context, values })
                }
              }, initialContactInfo)
            }
          })

          const {
            PlaceOrderResponse,
            PlaceOrderError,
            PlaceOrderCartUpdatedError
          } = dataByTypename(response.data.placeApplePayOrder)
          const error = PlaceOrderError || PlaceOrderCartUpdatedError
          if (error) {
            throw new Error(error.message)
          } else {
            deleteCartGuid()
            const { guid } = PlaceOrderResponse.completedOrder
            trackPurchase(PlaceOrderResponse.completedOrder)
            history.replace(getRestaurantPath(`confirm/${guid}`))
          }
        } catch (error) {
          setErrorMessage(error)
          throw error
        } finally {
          setSubmitting(false)
        }
      },
    [
      cart.guid,
      checkout,
      context,
      deleteCartGuid,
      getRestaurantPath,
      history,
      trackPurchase
    ]
  )

  if (loading || cartLoading || curbside.loading) return <Progress />
  const displayError = error || cartError || curbside.error
  if (displayError) return <ErrorComponent error={displayError} />
  const { creditCardConfig = {}, tfgConfig = {} } = cart.restaurant
  const { active, campaignName, campaignDescription, campaignLogoURL } =
    tfgConfig

  const isRoundUpEnabled = active

  setTfgConfig(tfgConfig)

  const canShowTip = creditCardConfig.tipEnabled
  const showDeliveryConsent =
    cart &&
    cart.deliveryProviderInfo &&
    cart.deliveryProviderInfo.needsDeliveryCommunicationConsent
  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting }) => {
        const { tipAmount } = Tip.getArgsForSubmit({ values, cart })
        const { tfgInput } = RoundUpForCharity.getArgsForSubmit({
          values,
          cart
        })
        const { estimatedRoundUpValue } = tfgInput
        const config = getConfig(tipAmount, estimatedRoundUpValue)
        const performTransaction = getPerformTransaction(values, {
          setSubmitting
        })
        const onCancel = () => setSubmitting(false)
        makeApplePayPayment({
          config,
          getMerchantSession,
          performTransaction,
          onCancel
        })
      }}
    >
      {({ values, isSubmitting, setFieldValue }) => (
        <Form>
          <>
            {!showDeliveryConsent && (
              <div className={styles.consent}>
                <SmsCommunicationConsent consentType='ApplePay' />
              </div>
            )}
            <Fieldset
              label={`Your Order (${cart.order.numberOfSelections})`}
              collapsable
              collapsed
            >
              <CartTable cart={cart} />
            </Fieldset>
            <CurbsidePickup.Component
              curbsidePickupConfig={curbsidePickupConfig}
            />
            <CheckoutDeliveryInfo.Component savedAddresses={savedAddresses} />
            {showDeliveryConsent && (
              <div className={styles.consent}>
                <DeliveryCommunicationConsent.Component cart={cart} />
              </div>
            )}
            {canShowTip && <Tip.Component />}
            {isRoundUpEnabled && (
              <RoundUpForCharity.Component
                cart={cart}
                campaignName={campaignName}
                campaignDescription={campaignDescription}
                campaignLogoURL={campaignLogoURL}
                tip={(canShowTip && parseFloat(values.paymentTip)) || 0}
                setFieldValue={setFieldValue}
              />
            )}
            <CartFooter
              showTip={canShowTip}
              tip={parseFloat(values.paymentTip) || null}
              cart={cart}
              showTotal
              showRoundUp={isRoundUpEnabled && values.isRoundUpForCharity}
              roundUp={values.roundUpAmount}
            />
            {errorMessage && <ErrorComponent error={errorMessage} />}
            <LegalCopy />
            <button
              disabled={isSubmitting}
              type='submit'
              className={styles.applePay}
              data-testid='apple-pay-checkout-button'
            />
          </>
        </Form>
      )}
    </Formik>
  )
}
